import React, { useState, useEffect } from "react";
import {
  FormControl,
  TextField,
  TextFieldProps,
  useTheme,
  InputAdornment,
} from "@mui/material";
import EuroIcon from "@mui/icons-material/Euro";
import { InputBaseComponentInterface } from "./types";
import { useTranslation } from "react-i18next";
import TooltipDialogCst from "@components/TooltipDialogCst";
import TooltipInput from "@components/InputCst/TooltipInput";

const EuroTextCst: React.FC<TextFieldProps & InputBaseComponentInterface> = (
  props,
) => {
  const theme = useTheme();
  const { t } = useTranslation(["input"]);

  const {
    id,
    label,
    value,
    placeholder = t("generic-placeholder") + label,
    onChange,
    customWidth,
    error = false,
    errorMessage = "",
    disabled,
    textTooltip,
    tooltipFile,
    tooltipFileIta,
    tooltipFileEn,
    sx,
    maxValue,
    min,
    max,
    isFilterActive,
    endAdornment,
    ...rest
  } = props;

  const [displayValue, setDisplayValue] = useState<string>("");
  const [isFocused, setIsFocused] = useState<boolean>(false);

  const formatNumber = (num: number) =>
    num.toLocaleString("it-IT", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  // sincronizza il valore esterno al display solo quando NON siamo in focus
  useEffect(() => {
    if (isFocused) return;
    if (value === undefined || value === null || value === "") {
      setDisplayValue("");
      return;
    }
    const n = Number(value);
    if (!isNaN(n)) setDisplayValue(formatNumber(n));
    else setDisplayValue("");
  }, [value, isFocused]);

  // rimuove i separatori migliaia e simboli (usato al focus per mostrare "raw")
  const unformatDisplay = (s: string) =>
    s ? s.replace(/\./g, "").replace(/[€\s]/g, "").trim() : "";

  const parseToNumber = (s: string) => {
    if (!s) return "";
    const normalized = s.replace(/\./g, "").replace(",", ".");
    const num = parseFloat(normalized);
    return isNaN(num) ? "" : num;
  };

  const handleFocus = () => {
    setIsFocused(true);
    // mostra la versione "raw" senza migliaia quando l'utente entra nel campo
    setDisplayValue((prev) => unformatDisplay(prev));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let raw = e.target.value ?? "";
    // converto punti in virgola (comodità per chi digita con il punto)
    raw = raw.replace(/\./g, ",");
    // mantengo solo cifre e una virgola
    raw = raw.replace(/[^0-9,]/g, "");
    const parts = raw.split(",");
    if (parts.length > 2) {
      // se ci sono più virgole, tieni solo la prima come separatore decimale
      raw = parts[0] + "," + parts.slice(1).join("");
    }
    setDisplayValue(raw);

    const num = parseToNumber(raw);
    // comunico al parent un event-like con il valore numerico pulito
    onChange?.({
      ...e,
      target: {
        ...e.target,
        value: num === "" ? "" : num,
      },
    } as any);
  };

  const handleBlur: React.FocusEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = (e) => {
    setIsFocused(false);
    const num = parseToNumber(displayValue);
    if (num === "") {
      setDisplayValue("");
      onChange?.({
        ...e,
        target: { ...e.target, value: "" },
      } as any);
      return;
    }
    setDisplayValue(formatNumber(num));
    onChange?.({
      ...e,
      target: { ...e.target, value: num },
    } as any);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      // forza la formattazione come onBlur
      // blur dell'elemento attiva handleBlur
      (e.target as HTMLElement).blur();
    }
    // evita input non desiderati come "e" in number
    if (e.key === "e" || e.key === "E") {
      e.preventDefault();
    }
    if (min !== undefined && Number(min) >= 0 && e.key === "-") {
      e.preventDefault();
    }
  };

  return (
    <FormControl
      variant="standard"
      sx={{
        display: "flex",
        alignItems: "center",
        width: customWidth ? customWidth : "auto",
        minWidth: "200px",
        "& .MuiFormLabel-root": {
          maxWidth: "calc(100% - 40px)",
          "&[data-shrink='true']": {
            maxWidth: "calc(133% - 40px)",
          },
        },
        "& .MuiInputBase-root fieldset": {
          borderColor: isFilterActive ? theme.palette.text.primary : "",
        },
        ...sx,
      }}
    >
      {textTooltip && (
        <TooltipDialogCst inputDialog title={label} textTooltip={textTooltip} />
      )}
      {tooltipFile && (
        <TooltipInput
          inputDialog
          hrefInput
          hrefIta={tooltipFileIta}
          hrefEn={tooltipFileEn}
          disabled={disabled}
        />
      )}

      <TextField
        {...rest}
        type="text" // importantissimo: TEXT, non number
        sx={{ width: "100%" }}
        size="small"
        error={error}
        helperText={
          <span
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              margin: 0,
            }}
          >
            {error ? errorMessage : ""}
          </span>
        }
        id={id}
        label={label}
        placeholder={placeholder}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <EuroIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        value={displayValue}
        onFocus={handleFocus}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        inputProps={{
          maxLength: maxValue,
          inputMode: "decimal",
          min: min ?? 0,
          max: max,
        }}
      />
    </FormControl>
  );
};

export default EuroTextCst;
